import React from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router-dom";

import { StyledEngineProvider } from "@mui/material/styles";

import "./styles/styles.scss";
import {
  ActivationListPage,
  ActivationPage,
  AudienceListPage,
  AudiencePage,
  CallbackPage,
  ComparePage,
  ExportPage,
  HomePage,
  InsightPage,
  LibraryPage,
  LibraryListPage,
  LibraryInsightPage,
  MindsetInsightPage,
  MindsetListPage,
  MindsetPage,
  NotFoundPage,
  PlaceholderPage,
  SiteListPage,
  SitePage,
  RecipePage,
  UnauthorizedPage,
  UploadListPage,
  UploadPage,
} from "@/pages";
import App from "./App";
import { BaseLayout } from "@/components/layouts";
import { ProtectedRoute } from "@/components/containers";
import { preloadClientComponents, getPageConfig } from "@/utils";

const router = createBrowserRouter(
  [
    {
      Component: App,
      children: [
        {
          path: "/",
          element: <ProtectedRoute component={BaseLayout} />,
          children: [
            {
              path: "",
              Component: HomePage,
            },
            {
              path: "callback",
              Component: CallbackPage,
            },
            {
              path: "audience",
              Component: AudienceListPage,
            },
            {
              path: "audience/:audienceId",
              Component: AudiencePage,
            },
            {
              path: "audience/:audienceId/insight",
              Component: InsightPage,
            },
            {
              path: "audience/:audienceId/export",
              Component: ExportPage,
            },
            {
              path: "upload",
              Component: UploadListPage,
            },
            {
              path: "upload/:audienceId",
              Component: UploadPage,
            },
            {
              path: "upload/:audienceId/insight",
              Component: InsightPage,
            },
            {
              path: "upload/:audienceId/export",
              Component: ExportPage,
            },
            {
              path: "activation",
              Component: ActivationListPage,
            },
            {
              path: "activation/:activationId",
              Component: ActivationPage,
            },
            {
              path: "activation/:activationId/recipe",
              Component: RecipePage,
            },
            {
              path: "mindset",
              Component: MindsetListPage,
            },
            {
              path: "mindset/:audienceId",
              Component: MindsetPage,
            },
            {
              path: "mindset/:audienceId/insight",
              Component: MindsetInsightPage,
            },
            {
              path: "mindset/:audienceId/export",
              Component: ExportPage,
            },
            {
              path: "library",
              Component: LibraryListPage,
            },
            {
              path: "library/:audienceId",
              Component: LibraryPage,
            },
            {
              path: "library/:audienceId/insight",
              Component: LibraryInsightPage,
            },
            {
              path: "library/:audienceId/export",
              Component: ExportPage,
            },
            {
              path: "measurement",
              Component: PlaceholderPage,
            },
            {
              path: "explore",
              Component: PlaceholderPage,
            },
            {
              path: "compare",
              Component: ComparePage,
            },
            {
              path: "site",
              Component: SiteListPage,
            },
            {
              path: "site/:audienceId",
              Component: SitePage,
            },
            {
              path: "site/:audienceId/insight",
              Component: InsightPage,
            },
            {
              path: "site/:audienceId/export",
              Component: ExportPage,
            },
            {
              path: "unauthorized",
              Component: UnauthorizedPage,
            },
            {
              path: "*",
              Component: NotFoundPage,
            },
          ],
        },
      ],
    },
  ],
  {
    future: {
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_relativeSplatPath: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

const container = document.getElementById("root")! as HTMLElement;
const root = createRoot(container);

const pageConfig = getPageConfig();
document.title = pageConfig.title;

async function startApp() {
  await preloadClientComponents(); // Preload client components before React renders

  root.render(
    <React.StrictMode>
      <StyledEngineProvider injectFirst>
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
      </StyledEngineProvider>
    </React.StrictMode>
  );
}

startApp();
